import { useId, useRef, useState, type ReactNode } from 'react';
import { Info, Search, SlidersHorizontal, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Dialog from './Dialog';
import Popover from './Popover';
import { useTouchMenus } from '../table/MenuButton';
import EntityPicker from './EntityPicker';
import GroupedOptions from './GroupedOptions';
import { activeFilterCount, isFilterActive, optionLabel, rangeText, splitInline } from '../table/filterModel';
import { formatDate } from '../../utils/format';

/**
 * List toolbar (DESIGN_SYSTEM.md §10.3, PRD §4.11, T-37).
 *
 * v1 (still supported): search · `children` (filter controls) · `end`.
 *
 * v2, calm by default (principle 0):
 *   [leading: view switcher] [search] [≤ 2 inline filters] [סינון (n)] … [end: page ⋯]
 *   [summary: count] [active-filter chips ✕] [ניקוי הכול] [מסונן בדף הנוכחי בלבד]
 * Every filter beyond the inline ones lives behind one "סינון" trigger
 * (popover on desktop, sheet on phones), drawn by its `kind`: toggle pills
 * (short lists, the default), a select, a searchable list (customers,
 * projects, workers) or a date / time range. A filter that only narrows the
 * rows already on this page (`clientSide`) is labelled so, honestly.
 */
export interface FilterOption {
  value: string;
  label: string;
  /** D-274 §1 — a group heading (e.g. "לקוחות" under "עבודה פנימית OTE"); absent = as before. */
  group?: string;
}

interface FilterDefBase {
  key: string;
  /** "סטטוס" */
  label: string;
  /** Narrows only the rows on the current page (shows the honest label). */
  clientSide?: boolean;
  /** No chip for this filter (another chip already explains it, e.g. "מסונן מדף הבית"). */
  hideChip?: boolean;
}

/** One value out of a list. */
export interface OptionFilterDef extends FilterDefBase {
  /** In the panel: `pills` (default, short lists), `select` (a native select), `search-select` (long lists: type to narrow). */
  kind?: 'pills' | 'select' | 'search-select';
  /** '' = no filter. */
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  /** Label of the "no filter" option ("כל הסטטוסים"). */
  allLabel?: string;
  /** `search-select`: placeholder of the search box. */
  searchPlaceholder?: string;
}

/** An inclusive range: two date (or time-of-day) fields; either end may be empty. Always in the panel. */
export interface RangeFilterDef extends FilterDefBase {
  kind: 'dateRange';
  from: string;
  to: string;
  /** One call per change (a URL update), with only the end that changed, or both when cleared. */
  onRangeChange: (patch: { from?: string; to?: string }) => void;
  fromLabel?: string;
  toLabel?: string;
  /** `date` (default) or `time` (time of day, HH:mm). */
  input?: 'date' | 'time';
}

export type FilterDef = OptionFilterDef | RangeFilterDef;

export interface FilterChip {
  key: string;
  label: string;
  onRemove: () => void;
  clientSide?: boolean;
}

interface FilterBarProps {
  /** Controlled search text; omit to hide the search field. */
  search?: string;
  onSearchChange?: (v: string) => void;
  searchPlaceholder?: string;
  /** v1: filter controls (selects, toggles), rendered after the search field. */
  children?: ReactNode;
  /** Trailing controls (v1: a view toggle or a count; v2: the page ⋯). */
  end?: ReactNode;
  className?: string;
  /** v2: before the search (the view switcher). */
  leading?: ReactNode;
  /** v2: filters; the first `inlineFilters` (max 2) option filters render inline, the rest behind "סינון". */
  filters?: FilterDef[];
  inlineFilters?: 0 | 1 | 2;
  /** v2: chips that are not one of `filters` (e.g. a view's condition, "מסונן מדף הבית"). */
  chips?: FilterChip[];
  /** v2: "ניקוי הכול" (shown with two or more chips). */
  onClearAll?: () => void;
  /**
   * v2: "ניקוי סינון" in the panel, as one update. Without it each active panel
   * filter is cleared on its own (fine for one; with URL state pass this).
   */
  onClearFilters?: () => void;
  /** v2: the result line at the start of the chip row ("866 קריאות"). */
  summary?: ReactNode;
}

/** One filter as a group of toggle pills (inside the "סינון" panel). */
function FilterPills({ def }: { def: OptionFilterDef }) {
  const { t } = useTranslation();
  const id = useId();
  const opts: FilterOption[] = [{ value: '', label: def.allLabel ?? t('list.filters.any') }, ...def.options];
  return (
    <fieldset className="min-w-0" aria-labelledby={`${id}-l`}>
      <legend id={`${id}-l`} className="mb-2 text-xs font-medium text-text-2">
        {def.label}
        {def.clientSide && <span className="font-normal"> · {t('list.filters.pageOnly')}</span>}
      </legend>
      <div className="flex flex-wrap gap-1.5">
        {opts.map((o) => {
          const on = def.value === o.value;
          return (
            <button
              key={o.value || '__all'}
              type="button"
              aria-pressed={on}
              onClick={() => def.onChange(o.value)}
              className={`inline-flex min-h-control-sm items-center rounded-full border px-3 text-sm transition-colors duration-micro touch-target ${
                on
                  ? 'border-transparent bg-accent-soft font-medium text-accent-soft-text'
                  : 'border-border bg-surface text-text-2 hover:bg-surface-subtle hover:text-text'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/** A native select (medium lists) inside the panel. */
function FilterSelect({ def }: { def: OptionFilterDef }) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-text-2">
        {def.label}
        {def.clientSide && <span className="font-normal"> · {t('list.filters.pageOnly')}</span>}
      </label>
      <select id={id} className="input" value={def.value} onChange={(e) => def.onChange(e.target.value)}>
        <option value="">{def.allLabel ?? t('list.filters.any')}</option>
        <GroupedOptions options={def.options} />
      </select>
    </div>
  );
}

/** An inclusive range: two native fields with LTR digits; each end bounds the other. */
function FilterRange({ def }: { def: RangeFilterDef }) {
  const { t } = useTranslation();
  const id = useId();
  const type = def.input ?? 'date';
  return (
    <fieldset className="min-w-0">
      <legend className="mb-1 text-xs font-medium text-text-2">
        {def.label}
        {def.clientSide && <span className="font-normal"> · {t('list.filters.pageOnly')}</span>}
      </legend>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor={`${id}-from`} className="mb-1 block text-xs text-text-2">{def.fromLabel ?? t('list.filters.from')}</label>
          <input id={`${id}-from`} type={type} dir="ltr" className="input text-start" value={def.from} max={def.to || undefined} onChange={(e) => def.onRangeChange({ from: e.target.value })} />
        </div>
        <div>
          <label htmlFor={`${id}-to`} className="mb-1 block text-xs text-text-2">{def.toLabel ?? t('list.filters.to')}</label>
          <input id={`${id}-to`} type={type} dir="ltr" className="input text-start" value={def.to} min={def.from || undefined} onChange={(e) => def.onRangeChange({ to: e.target.value })} />
        </div>
      </div>
    </fieldset>
  );
}

function PanelFilter({ def }: { def: FilterDef }) {
  const { t } = useTranslation();
  const labelId = useId();
  if (def.kind === 'dateRange') return <FilterRange def={def} />;
  if (def.kind === 'select') return <FilterSelect def={def} />;
  if (def.kind === 'search-select') {
    // FIX-B21 (D-214 QX-169): long lists in the filters use the same picker as the forms
    // (EntityPicker look and component). The filter itself is unchanged: '' = "all".
    return (
      <div className="min-w-0">
        <span id={labelId} className="mb-1 block text-xs font-medium text-text-2">{def.label}</span>
        <EntityPicker
          aria-labelledby={labelId}
          value={def.value}
          onChange={(v) => def.onChange(v)}
          emptyLabel={def.allLabel ?? t('list.filters.any')}
          clearable
          texts={def.searchPlaceholder ? { placeholder: def.searchPlaceholder } : undefined}
          options={def.options.map((o) => ({ value: o.value, label: o.label, ...(o.group ? { group: o.group } : {}) }))}
        />
      </div>
    );
  }
  return <FilterPills def={def} />;
}

function FilterPanel({ filters, onClear }: { filters: FilterDef[]; onClear?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4">
      {filters.map((f) => <PanelFilter key={f.key} def={f} />)}
      {onClear && (
        <div className="border-t border-border pt-3">
          <button type="button" className="btn btn-ghost btn-sm -ms-2" onClick={onClear}>{t('list.filters.clear')}</button>
        </div>
      )}
    </div>
  );
}

/** Clear one filter (one update). */
function clearFilter(f: FilterDef) {
  if (f.kind === 'dateRange') f.onRangeChange({ from: '', to: '' });
  else f.onChange('');
}

export default function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder,
  children,
  end,
  className = '',
  leading,
  filters,
  inlineFilters = 0,
  chips = [],
  onClearAll,
  onClearFilters,
  summary,
}: FilterBarProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelTitleId = useId();
  const touch = useTouchMenus();
  const v2 = !!(leading || filters || chips.length || summary);

  const { inline, panel: overflow } = splitInline(filters ?? [], inlineFilters);
  const activeOverflow = activeFilterCount(overflow);
  const formatRange = (f: RangeFilterDef) => rangeText(f.from, f.to, {
    both: (a, b) => t('list.filters.rangeBoth', { from: a, to: b }),
    since: (a) => t('list.filters.rangeSince', { from: a }),
    until: (b) => t('list.filters.rangeUntil', { to: b }),
  }, f.input === 'time' ? undefined : (v) => formatDate(v));

  const filterChips: FilterChip[] = (filters ?? [])
    .filter((f) => isFilterActive(f) && !f.hideChip)
    .map((f) => ({
      key: f.key,
      label: `${f.label}: ${f.kind === 'dateRange' ? formatRange(f) : optionLabel(f, f.value)}`,
      onRemove: () => clearFilter(f),
      clientSide: f.clientSide,
    }));
  const allChips = [...chips, ...filterChips];
  const clientSideActive = allChips.some((c) => c.clientSide);
  const clearOverflow = activeOverflow > 0
    ? onClearFilters ?? (() => overflow.forEach((f) => { if (isFilterActive(f)) clearFilter(f); }))
    : undefined;

  const searchField = onSearchChange && (
    <div className={`relative min-w-0 ${v2 ? 'flex-1 basis-48 sm:max-w-sm' : 'flex-1 min-w-48 max-w-md'}`}>
      <Search size={16} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-text-3" aria-hidden="true" />
      <input
        type="search"
        value={search ?? ''}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        // DQ-112: the browser's own clear button showed next to ours (two "×").
        className="input ps-9 pe-8 [&::-webkit-search-cancel-button]:appearance-none"
      />
      {search && (
        <button
          type="button"
          onClick={() => onSearchChange('')}
          aria-label={t('aria.clearSearch')}
          className="absolute end-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-text-3 hover:bg-surface-subtle hover:text-text"
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );

  if (!v2) {
    return (
      <div className={`flex flex-wrap items-center gap-2 ${className}`}>
        {searchField}
        {children}
        {end && <div className="flex items-center gap-2 ms-auto">{end}</div>}
      </div>
    );
  }

  const panel = <FilterPanel filters={overflow} onClear={clearOverflow} />;

  return (
    <div className={`flex flex-col gap-2.5 ${className}`}>
      <div className="flex flex-wrap items-center gap-2">
        {leading}
        {searchField}
        {inline.map((f) => f.kind !== 'dateRange' && (
          <select
            key={f.key}
            value={f.value}
            onChange={(e) => f.onChange(e.target.value)}
            aria-label={f.label}
            title={f.label}
            className="input w-auto min-w-32 flex-1 sm:max-w-56 sm:flex-none"
          >
            <option value="">{f.allLabel ?? t('list.filters.any')}</option>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ))}
        {children}
        {overflow.length > 0 && (
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={activeOverflow > 0 ? t('list.filters.triggerActive', { count: activeOverflow }) : t('list.filters.trigger')}
            className={`btn btn-secondary ${activeOverflow > 0 ? 'border-primary-200 bg-accent-soft text-accent-soft-text hover:bg-accent-soft' : ''}`}
          >
            <SlidersHorizontal size={16} strokeWidth={1.75} aria-hidden="true" />
            <span>{t('list.filters.trigger')}</span>
            {activeOverflow > 0 && <span className="tabular-nums">({activeOverflow})</span>}
          </button>
        )}
        {end && <div className="ms-auto flex items-center gap-2">{end}</div>}
      </div>

      {(summary || allChips.length > 0) && (
        <div className="flex min-h-control-sm flex-wrap items-center gap-x-2 gap-y-1.5">
          {summary && <div className="text-sm text-text-2 tabular-nums" aria-live="polite">{summary}</div>}
          {allChips.length > 0 && (
            <ul className="flex max-w-full flex-wrap items-center gap-1.5" aria-label={t('list.filters.activeLabel')}>
              {allChips.map((c) => (
                <li key={c.key} className="inline-flex max-w-full items-center gap-0.5 rounded-full bg-accent-soft pe-0.5 ps-3 text-sm text-accent-soft-text">
                  <span className="truncate py-1">{c.label}</span>
                  <button
                    type="button"
                    onClick={c.onRemove}
                    aria-label={t('list.filters.removeChip', { label: c.label })}
                    className="inline-flex size-7 shrink-0 items-center justify-center rounded-full hover:bg-primary-200 touch-target"
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {onClearAll && allChips.length > 1 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClearAll}>{t('list.filters.clearAll')}</button>
          )}
          {clientSideActive && (
            <span className="inline-flex items-center gap-1 text-xs text-text-2">
              <Info size={14} className="shrink-0" aria-hidden="true" />
              {t('list.filters.clientSide')}
            </span>
          )}
        </div>
      )}

      {overflow.length > 0 && (touch ? (
        <Dialog open={open} onClose={() => setOpen(false)} title={t('list.filters.title')} variant="sheet" detent="medium">
          {panel}
        </Dialog>
      ) : (
        <Popover
          open={open}
          onClose={(reason) => { setOpen(false); if (reason !== 'outside') triggerRef.current?.focus({ preventScroll: true }); }}
          anchorRef={triggerRef}
          align="start"
          role="dialog"
          ariaLabelledBy={panelTitleId}
          focusFirst
          className="w-80 p-4"
        >
          <h2 id={panelTitleId} className="mb-3 text-sm font-semibold text-text">{t('list.filters.title')}</h2>
          {panel}
        </Popover>
      ))}
    </div>
  );
}
